import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';

import { isConnectedToBrandAgencyOrganization } from 'utils/organization';
import { createReducer } from 'utils/redux';
import { API, get, patch } from 'utils/api';
import { logError } from 'utils/log';
import { Querystring } from 'utils/url';
import { __t, applyTranslations, t } from 'utils/language';
import { snakeCaseKeys } from 'utils/keys';
import { getPhone } from 'utils/helper/common';

applyTranslations(require('./organization-settings-reducer.i18n.json'));

import type { OrganizationSettingsForm, OrganizationSettingsParams, ThunkAction } from 'types';
import type { OrganizationSettingsState } from 'modules/organizations/components/types/OrganizationSettings.type';
import { AxiosRequestConfig } from 'axios';

const { ORGANIZATIONS } = API.END_POINTS;

const RECEIVE_ORGANIZATION = 'organizationSettings/RECEIVE_ORGANIZATION';
const RESET_ORGANIZATION = 'organizationSettings/RESET_ORGANIZATION';
const START_LOADING = 'organizationSettings/START_LOADING';
const STOP_LOADING = 'organizationSettings/STOP_LOADING';

const initialState: OrganizationSettingsState = {
  organization: {
    accountDetails: {},
    bankDetails: {},
    billingAddress: {},
    businessDetails: {},
    proofDocuments: {}
  },
  isLoading: false
};

function receiveSettings(organization) {
  return {
    type: RECEIVE_ORGANIZATION,
    payload: {
      organization
    }
  };
}

function startLoading() {
  return {
    type: START_LOADING
  };
}

function stopLoading() {
  return {
    type: STOP_LOADING
  };
}

export function resetOrganizationSettings() {
  return {
    type: RESET_ORGANIZATION
  };
}

export function fetchOrganizationSettings(
  organizationId: number,
  showLoader: boolean = true
): ThunkAction {
  const loaderId = 'fetchOrganizationSettings()';

  return async (dispatch, getState) => {
    dispatch(resetOrganizationSettings());

    if (showLoader) {
      dispatch(showLoading(loaderId));
      dispatch(startLoading());
    }

    try {
      const organization = getState().organizations.mapByOrganizationId[organizationId];

      // if the organization belongs to an agency then agency settings will be fetched instead
      const url = ORGANIZATIONS.getOrganizationSettings(
        isConnectedToBrandAgencyOrganization(organization) ?
          organization.agencyOrganizationId! : // agencyOrganizationId existence checked in isConnectedToBrandAgencyOrganization
          organizationId
      );

      const organizationSettingsResponse = await get(url);

      // In case of zalando meta, we want details of brand organization and not agency Organization
      const brandOrganizationSettingsUrl = ORGANIZATIONS.getOrganizationSettings(organizationId);
      const brandOrganizationSettingsResponse = await get(brandOrganizationSettingsUrl);

      const updatedOrganizationSettingsResponse = {
        ...organizationSettingsResponse.data,
        billingAddress: {
          ...organizationSettingsResponse.data.billingAddress,
          phone: getPhone(organizationSettingsResponse.data.billingAddress.phone)
        }
      };

      dispatch(receiveSettings({
        ...updatedOrganizationSettingsResponse,
        zalandoMeta: brandOrganizationSettingsResponse?.data?.zalandoMeta || { brands: [], merchants: [] }
      }));
    } catch (error) {
      logError(error);
    } finally {
      dispatch(receiveSettings({ id: organizationId }));

      if (showLoader) {
        dispatch(hideLoading(loaderId));
        dispatch(stopLoading());
      }
    }
  };
}

export function downloadProofDocuments(orgId: number, assetId: number) {
  const url = ORGANIZATIONS.downloadOrganizationProofDocument(orgId, assetId);

  const requestConfig: AxiosRequestConfig = {
    responseType: 'blob'
  };

  return async (dispatch) => {
    dispatch(showLoading(url));

    try {
      const response = await get(url, requestConfig);

      const fileURL = window.URL.createObjectURL(response.data);

      window.open(fileURL);
    } catch (error) {
      logError(error);

      dispatch(danger(__t('organization_settings_reducer.download_proof_document_error')));
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function saveOrganizationSettings(
  id: number,
  data: OrganizationSettingsForm,
  params?: OrganizationSettingsParams
): ThunkAction {
  let url = ORGANIZATIONS.updateOrganizationSettings(id);
  const queryString = Querystring.objectToString(snakeCaseKeys(params));

  if (queryString) {
    url = `${url}?${queryString}`;
  }

  return (dispatch) => {
    dispatch(showLoading(url));
    dispatch(startLoading());

    return patch(url, {
      data: {
        ...data,
        billingAddress: {
          ...data.billingAddress,
          phone: data.billingAddress.phone.number
        }
      }
    }).then((response) => {
      dispatch(hideLoading(url));
      dispatch(success(t('my_settings_save_success_message')));

      const updatedResponse = {
        ...response.data,
        billingAddress: {
          ...response.data.billingAddress,
          phone: getPhone(response.data.billingAddress.phone)
        }
      };

      dispatch(receiveSettings(updatedResponse));
    }).catch((error) => {
      let key = 'my_settings_save_error_message';

      if (error.response.status === 409) {
        key = 'my_settings_save_vat_invalid';
      }

      dispatch(danger(t(key)));
      dispatch(hideLoading(url));
      dispatch(stopLoading());
      logError(error);

      throw error;
    });
  };
}

export default createReducer<any, any>('organizationSettings', initialState, {
  [START_LOADING]: state => ({ ...state, isLoading: true }),
  [STOP_LOADING]: state => ({ ...state, isLoading: false }),
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET_ORGANIZATION]: () => initialState,
  [RECEIVE_ORGANIZATION]: (state, { organization }) => ({
    ...state,
    organization: {
      ...state.organization,
      ...organization
    },
    isLoading: false
  })
});
